import Box from "@material-ui/core/Box";
import { Theme } from "@material-ui/core/styles";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableContainer from "@material-ui/core/TableContainer";
import TableRow from "@material-ui/core/TableRow";
import { makeStyles } from "@material-ui/styles";
import { Form, Formik, FormikConfig } from "formik";
import React, { useContext } from "react";
import * as Yup from "yup";
import TesaTmbApi from "../../../api/services/TesaTmbApi";
import {
  TestMethodSearchMultipleAutocomplete, //
} from "../../../components/Autocomplete/CustomSearchAutoComplete.instances";
import CustomButton from "../../../components/Button/CustomButton";
import VerticalAlignedChip from "../../../components/Chip/VerticalAlignedChip";
import ConfirmationDialogContext from "../../../components/Dialog/ConfirmationDialog/ConfirmationDialogContext";
import isArrayUniqueChange from "../../../functions/isArrayFieldUniqueChange.function";
import removeElementAt from "../../../functions/removeElementAt.function";
import { tesaGrey1 } from "../../../styles/colors";
import TableListCell, { ClickTarget } from "./TableListCell";
import TestSpecificationMessage from "./TestSpecificationMessage";
import { AnalyzeHistoricalSpecificationData, MinimalSampleReference, MinimalTestMethod } from "./testTab.models";

const useStyles = makeStyles((theme: Theme) => ({
  buttonContainer: {
    "& > * + *": {
      marginLeft: theme.spacing(3),
    },
  },
  formContainer: {
    "& > * + *": {
      marginTop: theme.spacing(4),
    },
  },
  hint: {
    color: tesaGrey1,
    fontSize: "0.75rem",
  },
}));

export type AnalyzeHistoricalFormType = {
  productSamples: MinimalSampleReference[];
  substrateSamples: MinimalSampleReference[];
  testMethods: MinimalTestMethod[];
};

const DEPRECATED_MESSAGE =
  "Test methods from deprecated specifications have not been added. " +
  "Contact your lab’s key user to request an update of such specifications.";

interface AnalyzeHistoricalFormProps {
  cachedForm: AnalyzeHistoricalFormType | null;
  onSubmit: FormikConfig<AnalyzeHistoricalFormType>["onSubmit"];
  productSamples: MinimalSampleReference[];
  substrateSamples: MinimalSampleReference[];
  specificationData: AnalyzeHistoricalSpecificationData | null;
}

const AnalyzeHistoricalForm: React.FC<AnalyzeHistoricalFormProps> = ({
  cachedForm,
  productSamples,
  substrateSamples,
  specificationData,
  onSubmit: handleSubmit,
}) => {
  const { testMethods, deprecatedSpecs } = specificationData || { testMethods: [], deprecatedSpecs: [] };
  const classes = useStyles();
  const initialValues: AnalyzeHistoricalFormType = {
    productSamples,
    substrateSamples,
    testMethods,
  };
  const context = useContext(ConfirmationDialogContext);
  const tmbApi = new TesaTmbApi(context);

  return (
    <Formik initialValues={cachedForm || initialValues} validationSchema={Yup.object({})} onSubmit={handleSubmit}>
      {({ dirty, resetForm, setFieldValue, values }) => {
        const disabledReset = !dirty && !cachedForm;
        const disabledSubmit = Object.values(values).filter((vals) => vals.length).length < 2;
        const handleElementDelete = (target: ClickTarget<MinimalSampleReference>) => {
          const { name, index } = target;
          const fieldValues = values[name as keyof typeof values];
          if (fieldValues) {
            const deleted = removeElementAt(index, fieldValues);
            setFieldValue(name, deleted);
          }
        };

        return (
          <Form className={classes.formContainer}>
            <TableContainer>
              <Table>
                <TableBody>
                  <TableRow>
                    <TableListCell
                      component={VerticalAlignedChip}
                      mandatory
                      name="substrateSamples"
                      list={values.substrateSamples}
                      onElementDelete={handleElementDelete}
                      title="Substrate"
                    />
                    <TableListCell
                      component={VerticalAlignedChip}
                      mandatory
                      name="productSamples"
                      list={values.productSamples}
                      onElementDelete={handleElementDelete}
                      title="Product"
                    />
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
            <TestSpecificationMessage
              additionalText={{
                text: DEPRECATED_MESSAGE,
                showFor: "WARN",
              }}
              specifications={deprecatedSpecs}
              warn
            />
            <TestMethodSearchMultipleAutocomplete
              inputLabel="Test Method"
              keepTermOnChange
              mandatory
              map={(data) => ({ key: String(data.id), label: data.name })}
              name="testMethods"
              onChange={(e, newMethods) => {
                if (isArrayUniqueChange(values.testMethods, newMethods, "id")) {
                  setFieldValue("testMethods", newMethods);
                }
              }}
              optionFetch={(term) => tmbApi.fetchTestMethodVersions(term).then(({ data }) => data)}
              optionsCheckbox
              placeholder="Add Test Method"
              value={values.testMethods}
            />
            <p className={classes.hint}>*Define at least two properties</p>
            <Box className={classes.buttonContainer} display="flex" justifyContent="flex-end">
              <CustomButton
                disabled={disabledReset}
                text="Reset"
                onClick={() => resetForm(cachedForm ? { values: initialValues } : undefined)}
              />
              <CustomButton disabled={disabledSubmit} primary text="Analyze" type="submit" />
            </Box>
          </Form>
        );
      }}
    </Formik>
  );
};

export default AnalyzeHistoricalForm;
